/**
 * The Ledger. Season pass, legacy record, badge wall and your own money history.
 *
 * The reward track is the hero: a horizontal rail of tiers with a free lane and a premium
 * lane, the current tier held in a gold column. Everything else on the screen is a readout
 * of what the season owes you and what the seasons before it left behind.
 *
 * **EVERY ROW ON IT IS A SERVER READ NOW.** This screen was the last one in the game with no
 * server data at all, and what filled the gap was fifteen module-level TypeScript arrays. Three
 * of them were false in ways a player could act on, and the third one shipped:
 *
 *   - `STANDINGS` named twelve people. Nine are invented, one belongs to a sixteenth family that
 *     does not exist, and one gives a canon family's authored boss the wrong first name.
 *   - `BADGE_WALL` and `CHARTER_BADGES` built asset ids out of DISPLAY NAMES at render time,
 *     through `dyn()`, which widens the string past the compile-time check. One of seventeen
 *     names was real; the other sixteen were hash-picked by `nearest()` into a real but unrelated
 *     badge's artwork, so the wall drew other people's achievements with complete confidence.
 *   - `BUNDLE` put the Grey Fedora, which is the Rank Capo reward, and Verity Hill Chambers,
 *     which is a legendary Era-close reward, inside a $9.99 purchase.
 *
 * What replaces them is `GET /api/ledger`: `content/pass.json`, `content/rewards.json` and
 * `content/badges.json` behind the content gate, joined to `legend_era`, `legend_total`, `title`
 * and `hall_of_bosses` in the permanent database. Where there is nothing yet, the screen prints
 * the empty state and says what will be there. An honest empty room beats a full one with the
 * names painted on.
 */

import { useEffect, useRef, useState } from 'react'
import { A } from '../art'
import type {
  LedgerBadge,
  LedgerEntry,
  LedgerPassTier,
  LedgerPayload,
  LedgerReward,
  LedgerSurface,
} from '@ashport/shared'
import { Icon } from '../ui/Icon'
import { JEST } from '../build'
import { jestBeginPurchase, jestCompletePurchase } from '../jest'
import { api, ApiError } from '../net/api'
import { Art, Btn, Chip, Col, Meter, Panel, PanelHead, Rail, Row, Spacer, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import type { Rarity } from '../game/state'
import { clock, exactD, moneyD, useGame } from '../game/state'
import { useSession } from '../game/session'
import { eraByNumber } from '../game/eras'
import { Reveal } from './Reveal'

type TabId = 'sheet' | 'pass' | 'store' | 'legacy' | 'titles' | 'bosses' | 'money' | 'reveal'

/**
 * THE RAP SHEET IS FIRST, AND IT IS A RULING. DESIGN-DELTAS 1t.5.
 *
 * GAME-DESIGN 10.7 names the Rap Sheet as the player's own permanent page, and this screen already
 * held five sixths of it: the badge wall, the Era log, the lifetime standings, the Hall of Bosses,
 * the wardrobe and the pass track. What it lacked was a front page, and what the player lacked was
 * anywhere at all to read what they are.
 *
 * A tab rather than its own route, because a second screen would split one subject across two
 * places and leave a reader guessing which is authoritative, which is exactly the failure the
 * Phase 12 rewrite of this file existed to end. FIRST rather than fifth, because a screen whose
 * first tab is a shop is a shop.
 *
 * The PUBLIC page 10.7 describes is not built and is named at 1t.15: it is a privacy surface as
 * well as a status one, and this screen carries a spend cap and a purchase history that must never
 * be public.
 */
const TABS: { id: TabId; label: string }[] = [
  { id: 'sheet', label: 'Rap Sheet' },
  { id: 'pass', label: 'Season Pass' },
  { id: 'store', label: 'The Counter' },
  { id: 'legacy', label: 'Legacy' },
  { id: 'titles', label: 'Titles' },
  { id: 'bosses', label: 'Hall of Bosses' },
  { id: 'money', label: 'Money' },
  { id: 'reveal', label: 'The Reveal' },
]

/**
 * THE ONE PLACE A PURCHASE STARTS, shared by every Buy button on this screen.
 *
 * Three things happen and the order matters. `api.purchases()` mints the `ledger.purchase` action
 * token (this screen's own read does not carry one, deliberately: the Ledger is a pure read and a
 * token minted for it would be a token every unrelated button on the screen could spend).
 * `api.checkout()` exchanges it for a Stripe Session. Then the browser leaves.
 *
 * `window.location.assign` is the FIRST outbound navigation this application has ever performed.
 * There was no anchor tag, no `window.open` and no `location.href` write anywhere in `game/src`
 * before Phase 12, so this is the whole of the client's story about leaving.
 *
 * A REFUSAL IS SHOWN AND NOT SWALLOWED. `session.loadLedger` has no try/catch and this screen's
 * mount effect ends in `.catch(() => undefined)`, so a failed read renders the loading state
 * forever. That is survivable for a read and it is not survivable for a payment, so this returns
 * the message and the caller renders it, which is the pattern `Reveal.tsx` already uses.
 */
/**
 * What the counter says back. A refusal is red as it always was; under the Jest build a purchase
 * ends on this screen rather than on Stripe's, so the settle message comes back the same way and
 * is coloured by whether it landed.
 */
interface CounterLine {
  text: string
  tone: 'bad' | 'good'
}

async function startCheckout(sku: string): Promise<CounterLine | null> {
  if (JEST) return checkoutThroughJest(sku)
  try {
    const bundle = await api.purchases()
    const token = bundle.tokens?.token
    if (!token) return { text: 'The counter is not taking orders right now. Try again in a moment.', tone: 'bad' }
    const { url } = await api.checkout(token, sku)
    window.location.assign(url)
    return null
  } catch (e) {
    return { text: e instanceof ApiError ? e.message : 'That did not go through. Nothing was charged.', tone: 'bad' }
  }
}

/**
 * THE COUNTER THROUGH JEST. Phase 26, DESIGN-DELTAS 1ag.4.
 *
 * Their rule: payments only through the platform, so this never calls Stripe and never leaves
 * the page. Their flow, which is the server's too: begin, send the signed purchase to the server,
 * grant only after the server verified it, then complete. A cancel charges nothing and says so.
 * A refusal at the counter (the till shut, the cap, already owned) is the server's sentence in
 * voice; the purchase is left incomplete on the platform's side, which is what lets `App` bring
 * it back on the next open if the refusal was ours to lift, and what lets Jest refund it if not.
 */
async function checkoutThroughJest(sku: string): Promise<CounterLine> {
  try {
    const begun = await jestBeginPurchase(sku)
    if (begun === null) return { text: 'The counter could not reach Jest. Open the game from Jest and try again.', tone: 'bad' }
    if (begun.result === 'cancel') return { text: 'Nothing was charged.', tone: 'bad' }
    if (begun.result === 'error') return { text: 'Jest did not take that. Nothing was charged.', tone: 'bad' }
    const out = await api.jestPurchase({ purchaseSigned: begun.purchaseSigned })
    const line = out.results[0]
    const landed = line !== undefined && (line.result === 'settled' || line.result === 'already')
    if (landed) await jestCompletePurchase(begun.purchase.purchaseToken).catch(() => null)
    // Ownership, the wardrobe and the cap all moved. The store's read, so every tab agrees.
    await useSession.getState().loadLedger().catch(() => undefined)
    return { text: line?.message ?? 'Done. It is yours.', tone: landed ? 'good' : 'bad' }
  } catch (e) {
    return { text: e instanceof ApiError ? e.message : 'That did not go through. Nothing was charged.', tone: 'bad' }
  }
}

/**
 * The published spend cap, rendered BEFORE the button rather than after the refusal.
 *
 * GAME-DESIGN 12.1 prints "$200 per account per Era" on a public page, and Phase 11's Reveal set
 * the precedent that a player can always see the number applied to them. A cap a player only
 * meets by having a payment refused is a cap that takes money to explain itself.
 */
function CapMeter({ data }: { data: LedgerPayload }) {
  const used = data.cap.limit === 0 ? 0 : data.cap.spent / data.cap.limit
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="The spend cap" icon="ledger" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <Row gap={8}>
          <div className="t-mono" style={{ fontSize: 15, color: 'var(--ink-hi)' }}>
            ${data.cap.remainingUsd.toFixed(2)}
          </div>
          <div className="t-label" style={{ fontSize: sz(8.5), alignSelf: 'flex-end', paddingBottom: 2 }}>
            left of ${data.cap.limitUsd.toFixed(0)} this Era
          </div>
        </Row>
        <div style={{ marginTop: 7 }}>
          <Meter v={used} a="var(--gold)" />
        </div>
        <p style={{ margin: '8px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Ashport caps what one account can spend in an Era, on purpose, and publishes the number.
          Nothing you buy makes you stronger. It resets when the Era does.
        </p>
      </div>
    </Panel>
  )
}

/**
 * THE CHARTER, on the page, which is the only thing that makes GAME-DESIGN 12.1 true.
 *
 * 12.1 calls it "a public, permanent page, published before launch, with numbers on it" and argues
 * that in this genre such a page gets screenshotted and shared and is therefore free marketing.
 * Until Phase 12 it existed in a design document and nowhere a player could reach, which made the
 * marketing argument a claim about a page that did not exist.
 *
 * Read from the payload rather than typed here. The lines are authored in CONTENT-BIBLE 9.1 and
 * pinned line by line to a second independent copy inside `tools/content/validate.mjs`, so the
 * page and the promise cannot drift apart and a bullet cannot be quietly dropped.
 */
function Charter({ data }: { data: LedgerPayload }) {
  if (data.pass.charter.length === 0) return null
  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead title="The Charter" icon="ledger" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <p style={{ margin: '0 0 8px', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Permanent. It does not change without a dated note saying what moved.
        </p>
        <ul style={{ margin: 0, padding: '0 0 0 15px', listStyle: 'square' }}>
          {data.pass.charter.map((line) => (
            <li
              key={line}
              style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.55, marginBottom: 5 }}
            >
              {line}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  )
}

/** One thing on the counter. Every authored surface renders through this, not just two of them. */
function Offer({
  s,
  data,
  owned,
  busy,
  onBuy,
  explain = true,
}: {
  s: LedgerSurface
  data: LedgerPayload
  owned: boolean
  busy: string | null
  onBuy: (sku: string) => void
  /**
   * Whether THIS card carries the closed line.
   *
   * CONTENT-BIBLE 9.1 says "every surface on the screen renders with this line underneath it and
   * the button is dead", and the intent behind that sentence is that no dead button is ever
   * unexplained. On the Counter, which draws ten cards at once, ten copies of the same forty-word
   * sentence is a wall of grey that people stop reading, which defeats the intent while obeying
   * the letter. So the Counter states it ONCE, above the grid, where it covers every card; the
   * Season Pass tab, which shows two cards, still puts it under each of them.
   */
  explain?: boolean
}) {
  const items = s.grants
    .map((id) => data.rewards.find((r) => r.id === id))
    .filter((r): r is LedgerReward => r !== undefined)
  const affordable = data.cap.remainingUsd >= s.priceUsd
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title={s.label} icon={s.kind === 'subscription' ? 'clock' : 'crate'} accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        {items.length > 0 && (
          <Row gap={6} style={{ marginBottom: 8, alignItems: 'stretch' }}>
            {items.map((b) => (
              <div key={b.id} title={b.name} style={{ flex: 1, minWidth: 0 }}>
                <Art
                  src={A(b.artId, b.fallbackId)}
                  alt={b.name}
                  scrim="none"
                  style={{ height: 54, border: '1px solid var(--line-brass)' }}
                />
              </div>
            ))}
          </Row>
        )}
        <Row gap={6} style={{ justifyContent: 'center' }}>
          <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-faint)' }}>
            appearance only
          </span>
          {s.kind === 'subscription' && (
            <>
              <span style={{ color: 'var(--ink-faint)' }}>&middot;</span>
              <span className="t-label" style={{ fontSize: sz(8.5) }}>monthly, cancel any time</span>
            </>
          )}
        </Row>
        <Btn
          v="gold"
          size="sm"
          full
          style={{ marginTop: 9 }}
          disabled={s.closed || owned || busy !== null || !affordable}
          onClick={() => onBuy(s.id)}
        >
          {owned
            ? 'Yours'
            : s.closed
              ? 'Not for sale'
              : busy === s.id
                ? 'Opening the counter'
                : !affordable
                  ? 'Over the Era cap'
                  : `Buy, $${s.priceUsd.toFixed(2)}${s.kind === 'subscription' ? ' a month' : ''}`}
        </Btn>
        {explain && s.closed && data.pass.closedLine !== '' && (
          <p style={{ margin: '7px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            {data.pass.closedLine}
          </p>
        )}
        {!s.closed && !affordable && !owned && (
          <p style={{ margin: '7px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            You have ${data.cap.remainingUsd.toFixed(2)} left under this Era's cap.
          </p>
        )}
      </div>
    </Panel>
  )
}

/**
 * The Counter: every purchasable surface, the cap, and the wardrobe.
 *
 * SEVEN OF THE NINE AUTHORED SURFACES HAD NO RENDER PATH AT ALL before this. `content/pass.json`
 * carries a pass, a bundle, a subscription and seven direct purchases; the screen found the pass
 * and the bundle with `.find()` and drew nothing else. Priced, gated, walked by the content gate,
 * and invisible.
 */
function StoreTab({ data }: { data: LedgerPayload }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [fault, setFault] = useState<CounterLine | null>(null)
  const owned = new Set(data.wardrobe.map((w) => w.cosmeticId))

  const buy = (sku: string): void => {
    setFault(null)
    setBusy(sku)
    void startCheckout(sku).then((line) => {
      // Through Stripe a success has already left the page, so this only ever runs on a refusal.
      // Through Jest the purchase ends here, and the line is what the counter said.
      if (line) {
        setFault(line)
        setBusy(null)
      }
    })
  }

  const isOwned = (s: LedgerSurface): boolean =>
    s.kind === 'pass'
      ? data.pass.owned
      : s.grants.length > 0 && s.grants.every((id) => owned.has(id))

  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'flex-start' }}>
      <Col gap={8} style={{ width: 300, flex: 'none', maxHeight: '100%', overflowY: 'auto' }}>
        {/* The closed line, once, covering the whole grid. See `Offer`'s `explain` prop. */}
        {data.surfaces.some((s) => s.closed) && data.pass.closedLine !== '' && (
          <Panel style={{ flex: 'none' }}>
            <PanelHead title="The counter is shut" icon="ledger" accent="var(--brass-lit)" />
            <div style={{ padding: '9px 11px 11px', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
              {data.pass.closedLine}
            </div>
          </Panel>
        )}
        <CapMeter data={data} />
        <Charter data={data} />
        {fault && (
          <Panel tone={fault.tone === 'good' ? 'safe' : 'danger'} style={{ flex: 'none' }}>
            <div style={{ padding: '9px 11px', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
              {fault.text}
            </div>
          </Panel>
        )}
      </Col>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 8,
          overflowY: 'auto',
          alignContent: 'start',
        }}
      >
        {data.surfaces.map((s) => (
          <Offer key={s.id} s={s} data={data} owned={isOwned(s)} busy={busy} onBuy={buy} explain={false} />
        ))}
      </div>
    </Row>
  )
}

/**
 * The wardrobe. `cosmetic_entitlement`'s first reader, and until Phase 12 nothing in the client
 * knew what a player owned at all.
 *
 * `source` is HOW THEY GOT IT and it is shown rather than hidden, because that distinction is the
 * whole argument for a cosmetic-only shop: a bought hat and an earned hat are different objects
 * even when they are the same hat, and a wall that does not say which is which is a wall that
 * lets a purchase counterfeit prestige after the fact.
 */
const SOURCE_LABEL: Record<string, string> = {
  ledger: 'Ledger track',
  purchase: 'Bought',
  subscription: 'Donator',
  title: 'Title',
  era_reward: 'Earned',
  founder: 'Founder',
}

/**
 * The slot a cosmetic goes in, in words, so a wardrobe reads as a wardrobe rather than a grid.
 *
 * The nine are `cosmetic_entitlement`'s own CHECK in migration 0001. They are typed here rather
 * than derived because a slot is a schema fact, not authored canon: it is not in the content
 * bundle and the gate's canon-literal net does not cover it.
 */
const SLOT_LABEL: Record<string, string> = {
  suit: 'Suit',
  hat: 'Hat',
  car: 'Car',
  watch: 'Watch',
  trinket: 'Trinket',
  frame: 'Frame',
  vault: 'Vault',
  apartment: 'Apartment',
  office: 'Office',
}

function Wardrobe({ data }: { data: LedgerPayload }) {
  const busy = useSession((s) => s.busy)
  const equip = useSession((s) => s.equipCosmetic)
  if (data.wardrobe.length === 0) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="The wardrobe" icon="crate" accent="var(--brass-lit)" />
        <div style={{ padding: '11px 12px' }}>
          <FirstEra
            line="Nothing on the rail yet"
            note="Cosmetics arrive from the Ledger track, from the counter, and from things you do."
          />
        </div>
      </Panel>
    )
  }
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead title={`The wardrobe, ${data.wardrobe.length}`} icon="crate" accent="var(--brass-lit)" />
      <div
        style={{
          padding: '9px 11px 11px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: 7,
          overflowY: 'auto',
        }}
      >
        {data.wardrobe.map((w) => {
          const r = data.rewards.find((x) => x.id === w.cosmeticId)
          return (
            <div key={w.cosmeticId} title={r?.name ?? w.cosmeticId}>
              <Art
                src={r ? A(r.artId, r.fallbackId) : A('ui.avatar.socket_square')}
                alt={r?.name ?? w.cosmeticId}
                scrim="none"
                style={{
                  height: 58,
                  border: `1px solid ${w.equipped ? 'var(--gold)' : 'var(--line-brass)'}`,
                }}
              />
              <div className="t-label" style={{ fontSize: sz(8), marginTop: 3, color: 'var(--ink-dim)' }}>
                {SLOT_LABEL[w.slot] ?? w.slot} &middot; {SOURCE_LABEL[w.source] ?? w.source}
              </div>
              {/*
                THE BUTTON THAT PUTS IT ON, which did not exist. `equipped_cosmetic` had a reader,
                a refund path that deletes from it, and no INSERT anywhere in the repository, so
                this wall was a list rather than a wardrobe. DESIGN-DELTAS 1t.13.

                One slot holds one thing, so putting something on takes off whatever was there.
                The button says so by being the same button in both directions.
              */}
              <Btn
                size="sm"
                full
                v={w.equipped ? 'ghost' : 'gold'}
                disabled={busy}
                onClick={() => void equip(w.equipped ? null : w.cosmeticId, w.slot)}
                style={{ marginTop: 3 }}
              >
                {w.equipped ? 'TAKE OFF' : 'WEAR'}
              </Btn>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}

/*
 * `TRACK`, `CHALLENGES`, `ERA_GOALS`, `BADGE_WALL`, `CHARTER_BADGES`, `WALL`, `ERA_RUNS`,
 * `ERA_LOG`, `STANDINGS` and `BUNDLE` were all here. Every one is now a field on the payload.
 *
 * `CHALLENGES` and `ERA_GOALS` are deleted rather than wired, and that is a ruling rather than a
 * gap. Ruling 10.5 in `docs/PHASE-6B.md` settles how the pass track moves: a capped weekly pool
 * of 500 XP from actions the player was already taking, 125 XP per tier, carry-over capped at
 * 1,000, and in the same breath "no daily challenges, nothing time-gated". A "Weekly Challenges"
 * panel listing five timed objectives is the exact shape that ruling forbids, and five of the
 * eight objectives across the two arrays were for systems that do not exist (turf wars, dock
 * berths, rival soldiers on the floor).
 *
 * `TITLES` and `ALL_TITLES` went in Stage 2 with fourteen forbidden effect strings, and
 * `LEGACY_PERKS` with them. Ruling 10.1: a legacy object may change how you look, what you can
 * say, who you can talk to, and what the world remembers, and it may never change a number the
 * server rolls against, a cap, a rate, a price or a cooldown.
 */

const RAR: Record<Rarity, string> = {
  common: 'var(--r-common)',
  uncommon: 'var(--r-uncommon)',
  rare: 'var(--r-rare)',
  exceptional: 'var(--r-exceptional)',
  epic: 'var(--r-epic)',
  legendary: 'var(--r-legend)',
}

/* --------------------------------------------------------------- track cells */

type CellState = 'claimed' | 'ready' | 'locked'

const HDR_H = 24
const TILE_H = 54
const NAME_H = 15
const ROW_H = TILE_H + NAME_H + 4
const COL_W = 92

function Cell({
  r,
  state,
  on,
  onPick,
}: {
  r: LedgerReward | null
  state: CellState
  on: boolean
  onPick: () => void
}) {
  /*
   * A NULL SIDE IS A RUNG THE BIBLE HAS NOT AUTHORED, and it renders as an empty socket rather
   * than as a reward. `content/pass.json` carries 22 of the ruled 40 rungs because CONTENT-BIBLE
   * 9.2 placed 22, and the eighteen gaps are an authoring ask recorded in `docs/CONTENT-GAPS.md`.
   * Filling one here to make the rail look complete is inventing exactly the thing the whole
   * content gate exists to prevent.
   */
  if (!r) {
    return (
      <div
        title="Nothing is authored on this rung yet"
        style={{ height: ROW_H, display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <div
          style={{
            height: TILE_H,
            flex: 'none',
            border: '1px dashed var(--line-soft)',
            background: 'rgba(255,255,255,.015)',
          }}
        />
        <div className="t-label" style={{ fontSize: sz(8), height: NAME_H, color: 'var(--ink-faint)' }}>
          Not set
        </div>
      </div>
    )
  }
  const c = RAR[r.rarity]
  return (
    <div
      onClick={onPick}
      title={`${r.name} - ${r.rarity}`}
      style={{ cursor: 'pointer', height: ROW_H, display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Art
        src={A(r.artId, r.fallbackId)}
        alt={r.name}
        scrim="none"
        style={{
          height: TILE_H,
          flex: 'none',
          border: `1px solid ${on ? 'var(--gold-hot)' : state === 'locked' ? 'var(--line)' : `color-mix(in srgb, ${c} 48%, transparent)`}`,
          boxShadow: on ? '0 0 0 1px rgba(240,201,104,.35), 0 0 16px -6px var(--gold)' : undefined,
          transition: 'border-color 140ms ease, box-shadow 140ms ease',
        }}
      >
        {/*
          THE LOCK WAS PAINTING THE REWARD OUT, and this rail is the hero of the screen.

          It was `rgba(4,7,9,.6)`: sixty percent of near-black over a 54 pixel tile, on a wall the
          player is at tier 2 of 40 on, so thirty-eight of the forty tiles were covered. Thirteen of
          the twenty-three authored cosmetics are dark objects by their own art direction, a
          charcoal flannel suit, a black lacquered floor safe, a walnut wall panel, a charcoal silk
          tie, and those went to solid black. Measured off a real render, the covered tiles sat at a
          mean luminance of 7 to 24 out of 255. It read as a rail of empty boxes, which is how it
          was reported: "season pass is missing images".

          A quarter as much wash, and desaturated instead. The padlock in the corner is what says
          locked and it always was; the darkness was saying "broken". Grayscale is the honest signal
          for "not yours yet" because it removes the RARITY colour, which is the thing a locked
          reward is actually withholding, while leaving the object legible.
        */}
        {state === 'locked' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(4,7,9,.15)',
              backdropFilter: 'grayscale(0.7)',
              // Safari still wants the prefix, and a tile that renders in full colour there is a
              // cosmetic difference rather than a broken one, which is the right way to degrade.
              WebkitBackdropFilter: 'grayscale(0.7)',
              zIndex: 1,
            }}
          />
        )}
        {/* rarity strip: the tile always states its grade without a word of text */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: c, opacity: state === 'locked' ? 0.5 : 1, zIndex: 2 }} />
        <div style={{ position: 'absolute', top: 3, right: 3, zIndex: 2 }}>
          {state === 'locked' && <Icon n="lock" s={12} c="var(--ink-dim)" />}
          {state === 'claimed' && <Icon n="check" s={12} c="var(--dirty)" />}
          {state === 'ready' && (
            <span
              className="pulse"
              style={{ display: 'block', width: 7, height: 7, background: 'var(--gold-hot)', boxShadow: '0 0 8px var(--gold)' }}
            />
          )}
        </div>
      </Art>
      <div
        className="t-label"
        style={{
          fontSize: sz(8),
          letterSpacing: '.09em',
          height: NAME_H,
          color: state === 'locked' ? 'var(--ink-faint)' : 'var(--ink-mid)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {r.name}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- reward track */

function RewardTrack({
  data,
  sel,
  setSel,
}: {
  data: LedgerPayload
  sel: { t: number; lane: 'free' | 'prem' }
  setSel: (v: { t: number; lane: 'free' | 'prem' }) => void
}) {
  const rail = useRef<HTMLDivElement>(null)
  const nudge = (d: number) => rail.current?.scrollBy({ left: d, behavior: 'smooth' })

  const byId = new Map(data.rewards.map((r) => [r.id, r]))
  const at = data.pass.tier
  const owned = data.pass.owned
  const freeState = (t: number): CellState => (t < at ? 'claimed' : t === at ? 'ready' : 'locked')
  const premState = (t: number): CellState =>
    !owned ? 'locked' : t < at ? 'claimed' : t === at ? 'ready' : 'locked'

  const rewardOn = (row: LedgerPassTier | undefined, lane: 'free' | 'prem'): LedgerReward | null => {
    if (!row) return null
    const id = lane === 'free' ? row.free : row.premium
    return id ? (byId.get(id) ?? null) : null
  }

  const picked = data.pass.tiers.find((x) => x.tier === sel.t)
  const pickedR = rewardOn(picked, sel.lane)
  const shown = data.pass.tiers
  const span =
    shown.length === 0 ? '' : `Tiers ${shown[0].tier} to ${shown[shown.length - 1].tier}`

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title="Reward Track"
        icon="crate"
        sub={span}
        right={
          <Row gap={6}>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
              Tier {at} of {data.pass.tierCount}
            </span>
            <button onClick={() => nudge(-380)} title="Earlier tiers" style={railBtn}>
              <Icon n="chev" s={12} c="var(--ink-mid)" style={{ transform: 'rotate(180deg)' }} />
            </button>
            <button onClick={() => nudge(380)} title="Later tiers" style={railBtn}>
              <Icon n="chev" s={12} c="var(--ink-mid)" />
            </button>
          </Row>
        }
      />

      <Row gap={0} style={{ alignItems: 'stretch', padding: '8px 0 6px' }}>
        {/* lane labels */}
        <Col gap={6} style={{ width: 112, flex: 'none', padding: '0 8px 0 10px' }}>
          <div className="t-label" style={{ height: HDR_H, fontSize: sz(9), display: 'flex', alignItems: 'center' }}>Tier</div>
          <div style={{ height: ROW_H, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.16em' }}>Free</div>
            <div className="t-label" style={{ fontSize: sz(8), marginTop: 2 }}>Rewards</div>
          </div>
          <div style={{ height: ROW_H, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="t-head" style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.16em' }}>Premium</div>
            {owned ? <Chip c="var(--gold)">Pass Active</Chip> : null}
          </div>
        </Col>

        {/* the rail itself */}
        <div ref={rail} style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 2 }}>
          <Row gap={6} style={{ alignItems: 'stretch', width: 'max-content', padding: '0 8px' }}>
            {shown.map((row) => {
              const cur = row.tier === at
              return (
                <Col
                  key={row.tier}
                  gap={6}
                  style={{
                    width: COL_W,
                    flex: 'none',
                    padding: '0 4px 4px',
                    border: cur ? '1px solid var(--gold)' : '1px solid transparent',
                    background: cur
                      ? 'linear-gradient(180deg, rgba(214,168,75,.16), rgba(214,168,75,.03))'
                      : undefined,
                  }}
                >
                  <div
                    className="t-num"
                    style={{
                      height: HDR_H,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: cur ? 16 : 14,
                      color: cur ? 'var(--gold-hot)' : row.tier < at ? 'var(--ink-dim)' : 'var(--ink)',
                      borderBottom: '1px solid var(--line-soft)',
                    }}
                  >
                    {row.tier}
                  </div>
                  <Cell
                    r={rewardOn(row, 'free')}
                    state={freeState(row.tier)}
                    on={sel.t === row.tier && sel.lane === 'free'}
                    onPick={() => setSel({ t: row.tier, lane: 'free' })}
                  />
                  <Cell
                    r={rewardOn(row, 'prem')}
                    state={premState(row.tier)}
                    on={sel.t === row.tier && sel.lane === 'prem'}
                    onPick={() => setSel({ t: row.tier, lane: 'prem' })}
                  />
                </Col>
              )
            })}
          </Row>
        </div>
      </Row>

      {/* selection readout */}
      {pickedR && (
        <Row gap={9} style={{ padding: '6px 11px 8px', borderTop: '1px solid var(--line-soft)' }}>
          <span className="t-label" style={{ fontSize: sz(9), color: 'var(--gold)' }}>
            Tier {sel.t} {sel.lane === 'free' ? 'Free' : 'Premium'}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{pickedR.name}</span>
          <Chip r={pickedR.rarity}>{pickedR.rarity}</Chip>
          <Spacer />
          <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
            {(sel.lane === 'prem' ? premState(sel.t) : freeState(sel.t)) === 'claimed'
              ? 'Collected. It is in your locker.'
              : (sel.lane === 'prem' ? premState(sel.t) : freeState(sel.t)) === 'ready'
                ? 'Ready to collect.'
                : sel.lane === 'prem' && !owned
                  ? 'On the premium track.'
                  : `Reach tier ${sel.t} to collect.`}
          </span>
        </Row>
      )}
    </Panel>
  )
}

const railBtn: React.CSSProperties = {
  width: 20,
  height: 18,
  display: 'grid',
  placeItems: 'center',
  background: 'rgba(255,255,255,.04)',
  border: '1px solid var(--line)',
  cursor: 'pointer',
}

/* ------------------------------------------------------------------ sections */

/**
 * The banner for the Era the server says is running, not the one this file used to name.
 *
 * The number, the name, the banner and the headline rule all come from `content/eras.json` keyed
 * off `era.n` out of `/api/boot`. Before this, the heading read "Era 3: The Harbor Accord" as two
 * hardcoded strings on a screen whose own record table said something different, in a build whose
 * server was running Era 1.
 */
function EraBanner({ data }: { data: LedgerPayload }) {
  const { era } = useGame()
  /*
   * NO BORROWED PLATE. This read `?? FIRST_ERA`, so an Era the content does not name drew the
   * Iron Pact's banner under the Iron Pact's name and headline, with no error and no empty state.
   *
   * `boot.ts` and `/api/ledger` were both rewritten to REFUSE that case (`eraNamed` throws
   * MAINTENANCE, and `server/test/content.test.ts` asserts the refusal for 0, 7, -1 and 1.5), and
   * the client twin was left unguarded and untested. Adding a seventh Era would have made this
   * screen lie confidently. The honest answer on a screen is not a throw, it is a panel that says
   * what it does not know.
   */
  const canon = eraByNumber(era.n)
  if (canon === null) {
    return (
      <Panel tone="gold" style={{ height: 130, flex: 'none', display: 'grid', placeItems: 'center' }}>
        <div style={{ textAlign: 'center', padding: 12 }}>
          <div className="t-display" style={{ fontSize: 26, color: 'var(--ink-hi)' }}>
            Era {era.n}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-mid)', marginTop: 6 }}>
            This build has no record of that Era. Reload; if it stays, the client is older than the
            city.
          </div>
        </div>
      </Panel>
    )
  }
  return (
    <Panel tone="gold" style={{ height: 130, flex: 'none', position: 'relative', overflow: 'hidden' }}>
      <Art
        src={A(canon.bannerId)}
        alt={`The ${canon.name}, Ashport at the opening of the Era`}
        focal="55% 42%"
        style={{ position: 'absolute', inset: 0 }}
      />
      <Row gap={14} style={{ position: 'relative', flex: 1, padding: '0 12px' }}>
        <div style={{ minWidth: 0, textShadow: '0 2px 10px #000, 0 0 24px #000' }}>
          <div className="t-display" style={{ fontSize: 30, color: 'var(--ink-hi)', letterSpacing: '.04em' }}>
            Era {canon.n}:
          </div>
          <div className="t-display" style={{ fontSize: 30, color: 'var(--gold-hot)', letterSpacing: '.04em', marginTop: 2 }}>
            {canon.name}
          </div>
          <p style={{ margin: '9px 0 0', fontSize: 12, color: 'var(--ink-mid)' }}>
            {canon.headline}
          </p>
        </div>
        <Spacer />
        <Row
          gap={13}
          style={{
            flex: 'none',
            width: 400,
            alignSelf: 'center',
            padding: '11px 13px',
            background: 'linear-gradient(180deg, rgba(8,11,13,.9), rgba(4,7,9,.94))',
            border: '1px solid var(--line-brass)',
          }}
        >
          <div style={{ textAlign: 'center', flex: 'none' }}>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Tier</div>
            <div className="t-display" style={{ fontSize: 40, color: 'var(--gold)', lineHeight: 1 }}>
              {data.pass.tier}
            </div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {/*
              * THE XP NUMBERS ARE GONE, not deferred with a placeholder.
              *
              * This read "7,860 / 16,000 XP" beside a 49 percent meter, both hardcoded, on an
              * account holding no pass progress at all. Nothing writes `legend_era.pass_tier`
              * yet: the track's XP source ships with the payment integration in Phase 12,
              * because a pass nobody can buy does not need a way to advance. The rule that will
              * move it is published here instead, because ruling 10.5 is the thing worth knowing
              * and it is the opposite of a treadmill.
              */}
            <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
              Forty tiers, 125 XP each, from a capped weekly pool of 500 that comes off the things
              you were doing anyway. There are no daily challenges and nothing is timed. Two whole
              weeks can be missed and the track still finishes.
            </div>
            <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
              <span className="t-label" style={{ fontSize: sz(8.5) }}>
                {data.pass.tiers.length} of {data.pass.tierCount} rungs authored
              </span>
              <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-faint)' }}>
                Earned only
              </span>
            </Row>
          </div>
        </Row>
      </Row>
    </Panel>
  )
}

function LegendScore({ data }: { data: LedgerPayload }) {
  const marks = [
    { at: 10, k: '10K' },
    { at: 25, k: '25K' },
    { at: 50, k: '50K' },
    { at: 100, k: '100K' },
  ]
  const lifetime = data.legend.lifetime
  const pct = (lifetime / 100000) * 100
  return (
    <Panel style={{ width: 300, flex: 'none' }}>
      <PanelHead title="Legend Score" icon="legend" accent="var(--gold)" />
      <div style={{ padding: '10px 11px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Row gap={11}>
          <Icon n="legend" s={42} c="var(--gold)" />
          <div style={{ minWidth: 0 }}>
            <div className="t-display" style={{ fontSize: 30, color: 'var(--gold-hot)', lineHeight: 1 }}>
              {lifetime.toLocaleString()}
            </div>
            {/*
              * The percentile is GONE, not deferred, for the second time. `TopBar.tsx` records
              * the first: "Top 1.2% of all time" was a hardcoded string beside a REAL Legend
              * score, so a level two character holding zero Legend was told they were in the top
              * 1.2 percent. The standings are on this screen now, under the Hall of Bosses tab,
              * as real rows out of `legend_board`.
              */}
            <div style={{ fontSize: 11, color: 'var(--ink-mid)', marginTop: 4 }}>Lifetime, across every Era</div>
          </div>
        </Row>

        <div style={{ marginTop: 14, position: 'relative' }}>
          <Meter v={pct} a="var(--brass-deep)" b="var(--gold)" h={5} />
          <div style={{ position: 'relative', height: 26, marginTop: 3 }}>
            {marks.map((m) => (
              <div
                key={m.k}
                style={{
                  position: 'absolute',
                  left: `${m.at}%`,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                }}
              >
                <Icon n="target" s={10} c={pct >= m.at ? 'var(--gold)' : 'var(--ink-faint)'} />
                <div className="t-mono" style={{ fontSize: sz(9.5), color: pct >= m.at ? 'var(--gold)' : 'var(--ink-dim)', marginTop: 2 }}>
                  {m.k}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Spacer />
        <Row
          gap={9}
          style={{
            marginTop: 8,
            padding: 7,
            background: 'rgba(214,168,75,.07)',
            border: '1px solid var(--line-brass)',
          }}
        >
          {/* Legend is EARNED and its rewards are cosmetic. The reward at this threshold is a
              watch, it is authored, and the tile below reads it out of the payload rather than
              naming it in this file. */}
          <RewardAt data={data} id="skeleton_brass" note="Next at 100,000 lifetime Legend" />
        </Row>
      </div>
    </Panel>
  )
}

/** One reward tile named by id, drawn from the payload. No screen types a cosmetic's name. */
function RewardAt({ data, id, note }: { data: LedgerPayload; id: string; note: string }) {
  const r = data.rewards.find((x) => x.id === id)
  if (!r) return null
  return (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="t-label" style={{ fontSize: sz(8.5) }}>{note}</div>
        <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 2 }}>{r.name}</div>
      </div>
      <Art
        src={A(r.artId, r.fallbackId)}
        alt={r.name}
        scrim="none"
        style={{ width: 54, height: 40, flex: 'none', border: '1px solid var(--line-brass)' }}
      />
    </>
  )
}

/* ------------------------------------------------------------- other tabs */

/**
 * What a player in their FIRST Era sees on a page about the ones before it.
 *
 * The tone is the point. An empty legacy page is not a failure state, it is the first page of a
 * record that only grows, so it says what will be here rather than apologising for what is not.
 */
function FirstEra({ line, note }: { line: string; note: string }) {
  return (
    <Col
      gap={7}
      style={{
        flex: 1,
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '26px 18px',
        textAlign: 'center',
      }}
    >
      <Icon n="ledger" s={22} style={{ opacity: 0.4 }} />
      <div className="t-head" style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.12em' }}>{line}</div>
      <p style={{ margin: 0, maxWidth: 420, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>{note}</p>
    </Col>
  )
}

/**
 * THE RAP SHEET. What you are, what you have, what you have done. DESIGN-DELTAS 1t.5.
 *
 * The exit condition Phase 13 was set is that "a player can find, in one place, what they are,
 * what they have, what they have done, and what the next thing is". The first three are here; the
 * fourth is the panel on Home, because "what next" is a thing you act on and this is a thing you
 * read.
 *
 * **EVERY NUMBER ON IT HAS A WRITER, AND THAT IS WHY THE PANEL IS SHORT.** 1t.6 deleted
 * `reputation` and `sponsorFavor` from the wire rather than drawing them, because both were
 * published on every snapshot for thirteen phases with nothing writing either, and a record screen
 * is exactly the surface that would have drawn "reputation 50" for every account on earth. This
 * project has already caught that failure twice, at the Jobs rewards panel and at the hardcoded
 * "Top 1.2%".
 *
 * Legend is drawn because Phase 13 gave it an earner, and it says where the number came from in
 * words rather than leaving a zero to be interpreted.
 */
function SheetTab({ data }: { data: LedgerPayload }) {
  const { player } = useGame()
  const badgesHeld = data.badges.filter((b) => b.heldAt !== null).length
  const live = data.eras.find((e) => e.live) ?? null

  const Line = ({ k, v, c }: { k: string; v: string; c?: string }) => (
    <Row style={{ justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0' }}>
      <span className="t-label" style={{ fontSize: sz(9) }}>{k}</span>
      <span className="t-mono" style={{ fontSize: 12, color: c ?? 'var(--ink-hi)' }}>{v}</span>
    </Row>
  )

  return (
    <div className="scroll-y" style={{ flex: 1, minHeight: 0 }}>
      <Col gap={8}>
        <Panel>
          <PanelHead title="Who you are" icon="rank" accent="var(--gold)" sub={live?.name} />
          <div style={{ padding: '10px 12px 12px' }}>
            <Row gap={12} style={{ alignItems: 'flex-start' }}>
              <Art
                src={A(player.portraitId as Parameters<typeof A>[0], 'ui.avatar.socket_square')}
                alt={player.name === '' ? player.handle : player.name}
                scrim="none"
                style={{ width: 84, height: 84, flex: 'none', border: '1px solid var(--line-brass)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-head" style={{ fontSize: 17, color: 'var(--gold)', letterSpacing: '.05em' }}>
                  {player.name === '' ? player.handle : player.name}
                </div>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 2 }}>
                  {player.handle}
                </div>
                <Line k="Rank" v={player.rank} />
                <Line k="Level" v={`${player.level}`} />
                <Line k="Family" v={player.family === '' ? 'None' : player.family} />
                <Line
                  k="Spoken for by"
                  v={player.sponsor === '' ? 'Nobody yet' : player.sponsor}
                  c={player.sponsor === '' ? 'var(--ink-dim)' : 'var(--ink-hi)'}
                />
              </div>
            </Row>
          </div>
        </Panel>

        <Panel tone="gold">
          <PanelHead title="Legend" icon="legend" accent="var(--gold)" sub="Cumulative, forever" />
          <div style={{ padding: '10px 12px 12px' }}>
            <Row gap={20}>
              <div>
                <div className="t-label" style={{ fontSize: sz(9) }}>This Era</div>
                <div className="t-num" style={{ fontSize: 26, color: 'var(--ink-hi)' }}>
                  {data.legend.era.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="t-label" style={{ fontSize: sz(9) }}>Lifetime</div>
                <div className="t-num" style={{ fontSize: 26, color: 'var(--gold)' }}>
                  {data.legend.lifetime.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="t-label" style={{ fontSize: sz(9) }}>Eras</div>
                <div className="t-num" style={{ fontSize: 26, color: 'var(--ink-hi)' }}>
                  {data.legend.erasPlayed}
                </div>
              </div>
            </Row>
            {/*
              THE ZERO IS EXPLAINED IN WORDS, and the words are true rather than a placeholder.
              DESIGN-DELTAS 1t.7: Legend has exactly one earner in this phase, it is the sponsor's
              row out of GAME-DESIGN 6.3's own table, and the rest are named as not built. A player
              looking at a zero deserves to know which of those two it is.
            */}
            <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
              Legend survives the wipe when nothing else does. One thing pays it today: speak for
              somebody, and when they make Soldier you are paid for it. Nothing you can do alone
              earns a point, and that is deliberate.
            </p>
          </div>
        </Panel>

        <Panel>
          <PanelHead title="What you have done" icon="trophy" />
          <div style={{ padding: '10px 12px 12px' }}>
            <Line k="Badges held" v={`${badgesHeld} of ${data.badges.length}`} />
            <Line k="Cosmetics owned" v={`${data.wardrobe.length}`} />
            <Line k="Wearing" v={`${data.wardrobe.filter((w) => w.equipped).length}`} />
            <Line k="Pass tier" v={`${data.pass.tier} of ${data.pass.tierCount}`} />
            {data.eras.length > 0 && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--line-soft)', paddingTop: 8 }}>
                <div className="t-label" style={{ fontSize: sz(9), marginBottom: 5 }}>The Era log</div>
                {data.eras.map((e) => (
                  <Row key={e.n} style={{ justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ fontSize: 11.5, color: e.live ? 'var(--gold)' : 'var(--ink-mid)' }}>
                      {e.name}
                    </span>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                      {e.rankFinal} &middot; level {e.levelFinal} &middot; {e.legend.toLocaleString()} legend
                    </span>
                  </Row>
                ))}
              </div>
            )}
          </div>
        </Panel>
      </Col>
    </div>
  )
}

function LegacyTab({ data }: { data: LedgerPayload }) {
  /*
   * Only an Era that has actually CLOSED has a row, which the server marks with `live: false`.
   * In Era 1 that is none of them, by definition, which is what makes the empty state the
   * ordinary case rather than an edge one.
   */
  const closed = data.eras.filter((e) => !e.live)
  return (
    <Col gap={8} style={{ flex: 1, minHeight: 0 }}>
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="Era Record" icon="ledger" sub="Every season you have run" />
        {closed.length === 0 ? (
          <FirstEra
            line="Nothing has closed yet"
            note="This is the first one. In twelve weeks it takes a line on this page with your rank at the close, the tier you reached and the Legend you kept, and nothing after that ever takes it off again."
          />
        ) : (
        <div className="scroll-y" style={{ flex: 1 }}>
          <table className="grid-tbl">
            <thead>
              <tr>
                <th style={{ width: 250 }}>Era</th>
                <th>Family</th>
                <th>Rank at close</th>
                <th>Tier reached</th>
                <th style={{ textAlign: 'right' }}>Legend earned</th>
                <th style={{ textAlign: 'right' }}>Badges</th>
              </tr>
            </thead>
            <tbody>
              {closed.map((e) => (
                <tr key={e.n}>
                  <td>
                    <Row gap={9}>
                      <Art
                        src={A(e.plateId)}
                        alt={e.name}
                        scrim="none"
                        style={{ width: 52, height: 32, flex: 'none', border: '1px solid var(--line)' }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="t-head" style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: '.1em' }}>Era {e.n}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{e.name}</div>
                      </div>
                    </Row>
                  </td>
                  <td style={{ color: 'var(--ink-mid)' }}>{e.familyName ?? 'No family'}</td>
                  <td style={{ color: 'var(--ink)' }}>{e.rankFinal}</td>
                  <td className="t-mono">{e.passTier} / {data.pass.tierCount}</td>
                  <td className="t-num" style={{ textAlign: 'right', color: 'var(--gold)' }}>{e.legend.toLocaleString()}</td>
                  <td className="t-mono" style={{ textAlign: 'right', color: 'var(--ink-mid)' }}>{e.badges}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </Panel>
      <Panel style={{ flex: 'none' }}>
        <PanelHead title="What Carries Over" icon="rep" sub="And what does not" />
        <p
          style={{
            margin: 0,
            padding: '10px 12px 12px',
            fontSize: 11.5,
            color: 'var(--ink-mid)',
            lineHeight: 1.55,
          }}
        >
          Legacy is a record and never a head start. What survives an Era is the badge, the title
          and the name in the Hall, and none of them moves a number. Everybody starts where you
          start.
        </p>
      </Panel>
    </Col>
  )
}

/**
 * The Titles tab, which says what a title IS instead of inventing ten of them.
 *
 * The tab stays. Removing it would delete a canon system from the UI rather than admit it is
 * unbuilt, and GAME-DESIGN 10.5 is real: titles exist, they are earned, and they are stamped with
 * the Era they were won in. What does NOT exist is a single authored title anywhere in the corpus,
 * an art namespace to draw one, or a content file to hold one, so there is nothing honest to list.
 */
function TitlesTab() {
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead title="Permanent Titles" icon="rank" sub="Earned across every era" />
      <FirstEra
        line="No title yet"
        note="A title is what the city calls you afterwards, and it carries the Era you earned it in for as long as your name exists. It is a record and never an advantage: no title moves a number, a cap, a rate or a price. The first one goes here the day you do something worth being called something for."
      />
    </Panel>
  )
}

/**
 * The Hall of Bosses, and the lifetime standings under it.
 *
 * Two different tables that used to be one. `hall_of_bosses` is twelve rows per Era, one per
 * district, written at rollover; `legend_total` is the lifetime board that `legend_board`
 * indexes. The old screen rendered twelve invented people under the Hall's header and called it
 * both things at once.
 */
function BossesTab({ data }: { data: LedgerPayload }) {
  return (
    <Col gap={8} style={{ flex: 1, minHeight: 0 }}>
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="Hall of Bosses" icon="trophy" sub="One seat per district, per Era" />
        {data.bosses.length === 0 ? (
          <FirstEra
            line="No seat has been taken yet"
            note="Twelve districts, one Boss each, recorded the moment an Era closes and never moved afterwards. The first twelve names go up in twelve weeks."
          />
        ) : (
          <div className="scroll-y" style={{ flex: 1 }}>
            <table className="grid-tbl">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Era</th>
                  <th>Boss</th>
                  <th>Family</th>
                </tr>
              </thead>
              <tbody>
                {data.bosses.map((b) => (
                  <tr key={`${b.era}:${b.districtId}`}>
                    <td className="t-num" style={{ color: 'var(--gold)' }}>{b.era}</td>
                    <td style={{ color: 'var(--ink-hi)' }}>{b.handle}</td>
                    <td style={{ color: 'var(--ink-mid)' }}>{b.familyName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead
          title="Standings"
          icon="legend"
          sub="Lifetime Legend, every Era"
          right={
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
              {data.standings.length} shown
            </span>
          }
        />
        <div className="scroll-y" style={{ flex: 1 }}>
          <table className="grid-tbl">
            <thead>
              <tr>
                <th style={{ width: 46 }}>#</th>
                <th>Handle</th>
                <th style={{ textAlign: 'right' }}>Legend</th>
                <th style={{ textAlign: 'right' }}>Eras played</th>
              </tr>
            </thead>
            <tbody>
              {data.standings.map((b) => (
                <tr key={b.n} data-on={b.me || undefined}>
                  <td className="t-num" style={{ color: b.n <= 3 ? 'var(--gold)' : 'var(--ink-dim)' }}>{b.n}</td>
                  <td style={{ color: b.me ? 'var(--gold)' : 'var(--ink-hi)' }}>{b.handle}</td>
                  <td className="t-num" style={{ textAlign: 'right' }}>{b.legend.toLocaleString()}</td>
                  <td className="t-mono" style={{ textAlign: 'right', color: b.erasPlayed ? 'var(--gold)' : 'var(--ink-faint)' }}>
                    {b.erasPlayed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </Col>
  )
}

/**
 * Your own money history, and the first caller `api.moneyHistory` has ever had.
 *
 * Append-only and complete: for each currency the deltas sum to the balance, and there is no path
 * anywhere in the game that moves money without writing one of these. That is the promise the
 * whole economy rests on, and it is a promise a player can check for themselves, which is the
 * only reason this is a player-facing screen rather than an admin one.
 *
 * The cursor is BOTH halves. Laundering writes a dirty row and a clean row in the same
 * millisecond by construction, so a timestamp is not a unique position: a cursor carrying only
 * `at` skipped every remaining row at that instant when a page boundary fell inside it, and a
 * client adding up its pages reached a different total than the server did.
 */
function MoneyTab() {
  const page = useSession((s) => s.moneyHistory)
  const more = useSession((s) => s.loadMoneyHistory)
  const [busy, setBusy] = useState(false)

  if (!page) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="Money" icon="ledger" sub="Every cent of both balances" />
        <FirstEra line="Reading the books" note="One moment." />
      </Panel>
    )
  }

  const off = (a: number, b: number) => a !== b
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Money"
        icon="ledger"
        sub="Every cent of both balances, oldest entry to newest"
        right={
          <Row gap={9}>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--dirty)' }}>
              Dirty {moneyD(page.balance.dirty / 100)}
            </span>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--gold)' }}>
              Clean {moneyD(page.balance.clean / 100)}
            </span>
          </Row>
        }
      />
      {/*
        * THE RECONCILIATION, ON SCREEN.
        *
        * `totals` sums every row of the whole history and `balance` is what the account actually
        * holds. They are equal unless something moved money without writing a row, which is the
        * one thing the ledger promise forbids. Showing the mismatch rather than hiding it is the
        * point: a player who can check is a player who does not have to trust.
        */}
      {(off(page.totals.dirty, page.balance.dirty) || off(page.totals.clean, page.balance.clean)) && (
        <div
          style={{
            margin: '6px 11px 0',
            padding: '6px 8px',
            border: '1px solid var(--line-brass)',
            background: 'rgba(214,168,75,.07)',
            fontSize: 11,
            color: 'var(--ink-mid)',
          }}
        >
          The rows and the balance do not agree. Every movement of money is supposed to be a row
          in here, so this is worth reporting: rows say {exactD(page.totals.dirty / 100)} dirty and{' '}
          {exactD(page.totals.clean / 100)} clean.
        </div>
      )}
      <div className="scroll-y tbl-wrap" style={{ flex: 1 }}>
        <table className="grid-tbl">
          <thead>
            <tr>
              <th style={{ width: 150 }}>When</th>
              <th style={{ width: 80 }}>Purse</th>
              <th>What moved it</th>
              <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
              <th style={{ textAlign: 'right', width: 130 }}>Balance after</th>
            </tr>
          </thead>
          <tbody>
            {page.rows.map((r: LedgerEntry) => (
              <tr key={r.id}>
                <td className="t-mono" style={{ color: 'var(--ink-dim)' }}>
                  {new Date(r.at).toISOString().slice(0, 16).replace('T', ' ')}
                </td>
                <td style={{ color: r.currency === 'dirty' ? 'var(--dirty)' : 'var(--gold)' }}>
                  {r.currency === 'dirty' ? 'Dirty' : 'Clean'}
                </td>
                {/* The reason CODE, lowercased and spaced. It is not authored prose and it is not
                    pretended to be: an authored line per reason is a CONTENT-GAPS entry, and a
                    made-up sentence per reason would be canon invented in a screen. */}
                <td style={{ color: 'var(--ink-mid)' }}>{r.reason.toLowerCase().replace(/_/g, ' ')}</td>
                <td
                  className="t-num"
                  style={{ textAlign: 'right', color: r.delta >= 0 ? 'var(--dirty)' : 'var(--ink-hi)' }}
                >
                  {r.delta >= 0 ? '+' : ''}
                  {exactD(r.delta / 100)}
                </td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--ink-dim)' }}>
                  {exactD(r.balance / 100)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {page.rows.length === 0 && (
          <FirstEra
            line="Nothing has moved yet"
            note="Every payout, every fee, every wash and every confiscation takes a line here the moment it happens, and nothing ever edits one. Run a job and the first row appears."
          />
        )}
      </div>
      {page.cursor !== null && page.cursorId !== null && (
        <div style={{ padding: '6px 11px 10px' }}>
          <Btn
            v="ghost"
            size="sm"
            full
            disabled={busy}
            onClick={() => {
              setBusy(true)
              void more({ before: page.cursor as number, beforeId: page.cursorId as string }).finally(
                () => setBusy(false),
              )
            }}
          >
            {busy ? 'Reading' : 'Older entries'}
          </Btn>
        </div>
      )}
    </Panel>
  )
}

/* ------------------------------------------------------------- right column */

/**
 * The one paid bundle, read out of `content/pass.json` rather than typed into this file.
 *
 * What was here: three `KnownAssetId` literals, one of which was the Grey Fedora, whose authored
 * source is "Reach Rank Capo in any Era", priced at $9.99 under a header reading "appearance
 * only". Selling the Capo hat to somebody who never made Capo is the precise thing MONETIZATION
 * argues a purchase must never do, and there was no check that could see it, because the
 * monetisation walk tested `kind === 'cosmetic'` and a hat is a cosmetic.
 *
 * Now: `rewards.json` carries a `source` per row, the gate refuses `earned` or `default` on any
 * surface money can reach, and this component renders whatever survived that check.
 */
function Highlight({ data, onBuy, busy }: { data: LedgerPayload; onBuy: (sku: string) => void; busy: string | null }) {
  const bundle: LedgerSurface | undefined = data.surfaces.find((s) => s.kind === 'bundle')
  if (!bundle) return null
  const items = bundle.grants
    .map((id) => data.rewards.find((r) => r.id === id))
    .filter((r): r is LedgerReward => r !== undefined)
  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead title="Premium Highlight" icon="crown" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <div className="t-label" style={{ fontSize: sz(8.5), textAlign: 'center', color: 'var(--gold)' }}>
          Exclusive this season
        </div>
        <div className="t-display" style={{ fontSize: 19, color: 'var(--ink-hi)', textAlign: 'center', marginTop: 4 }}>
          {bundle.label}
        </div>
        <Row gap={6} style={{ marginTop: 9, alignItems: 'stretch' }}>
          {items.map((b) => (
            <div key={b.id} title={b.name} style={{ flex: 1, minWidth: 0 }}>
              <Art
                src={A(b.artId, b.fallbackId)}
                alt={b.name}
                scrim="none"
                style={{ height: 62, border: '1px solid var(--line-brass)' }}
              />
            </div>
          ))}
        </Row>
        <Row gap={6} style={{ marginTop: 8, justifyContent: 'center' }}>
          <span className="t-label" style={{ fontSize: sz(8.5) }}>{items.length} items</span>
          <span style={{ color: 'var(--ink-faint)' }}>&middot;</span>
          <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-faint)' }}>
            appearance only
          </span>
        </Row>
        {/*
          * WIRED. This button and the one in `PassOffer` rendered a price and a disabled state
          * and had no `onClick` at all, through every phase that shipped this screen. They were
          * the two buy-shaped things in the client and neither of them could ever have taken a
          * tap, which is why `docs/PHASE-12-STRIPE.md`'s claim that "neither needs editing to
          * open" was wrong: deleting the Closed line would have produced two enabled, inert
          * buttons. DESIGN-DELTAS 1s.2.
          */}
        <Btn
          v="gold"
          size="sm"
          full
          style={{ marginTop: 9 }}
          disabled={bundle.closed || busy !== null}
          onClick={() => onBuy(bundle.id)}
        >
          {bundle.closed
            ? 'Not for sale'
            : busy === bundle.id
              ? 'Opening the counter'
              : `Buy, $${bundle.priceUsd.toFixed(2)}`}
        </Btn>
        {bundle.closed && data.pass.closedLine !== '' && (
          <p
            style={{
              margin: '7px 0 0',
              fontSize: sz(10.5),
              color: 'var(--ink-dim)',
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            {data.pass.closedLine}
          </p>
        )}
      </div>
    </Panel>
  )
}

/**
 * The badge wall: all 48, from `content/badges.json`, grouped the way the content groups them.
 *
 * The filter argument in `docs/PHASE-6B.md` item 0.7 is settled by the read rather than by an
 * operator. There is no Era filter at all any more, because a badge is not owned BY an Era: it
 * is permanent, it has a condition, and either this player holds it or they do not. What the
 * filter was standing in for was "which of these are real", and the answer is now all of them.
 *
 * The `charter_member` naming question, recorded in CONTENT-GAPS, is answered by deletion:
 * `CHARTER_BADGES` rendered five names of which none is one of GAME-DESIGN 10.6's 48, and
 * `founding_era` is the real badge with the real condition and the real art. It is in the list
 * below like the other 47.
 */
function BadgeWall({ badges }: { badges: LedgerBadge[] }) {
  const groups: { name: string; rows: LedgerBadge[] }[] = []
  for (const b of badges) {
    const g = groups.find((x) => x.name === b.group)
    if (g) g.rows.push(b)
    else groups.push({ name: b.group, rows: [b] })
  }
  const held = badges.filter((b) => b.heldAt !== null).length
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Legacy Badges"
        icon="trophy"
        sub="Permanent, across every Era"
        right={
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
            {held} of {badges.length}
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '8px 10px 10px' }}>
        {held === 0 && (
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            None of them yet. Every one below is permanent and every one states its own condition,
            so nothing here is a surprise. They are awarded when the Era closes.
          </p>
        )}
        {groups.map((g) => (
          <div key={g.name} style={{ marginBottom: 12 }}>
            <Row style={{ justifyContent: 'space-between' }}>
              <span className="t-head" style={{ fontSize: sz(10), color: 'var(--gold)', letterSpacing: '.13em' }}>
                {g.name}
              </span>
              <span className="t-label" style={{ fontSize: sz(8) }}>
                {g.rows.filter((b) => b.heldAt !== null).length} of {g.rows.length}
              </span>
            </Row>
            <div className="rule" style={{ margin: '5px 0 7px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 5 }}>
              {g.rows.map((b) => (
                <div
                  key={b.id}
                  title={b.heldAt !== null ? `${b.name}: held` : `${b.name}: ${b.condition}`}
                  style={{ minWidth: 0, cursor: 'help' }}
                >
                  {/*
                    * `A(b.artId)` with the id STRAIGHT OFF THE PAYLOAD, never built from a display
                    * name. The old line was `A(dyn('badge.' + name.toLowerCase()...))`, and `dyn()`
                    * widens the string past the compile-time check, so `nearest()` hash-picked a
                    * real sibling badge for every one of the sixteen names that did not resolve.
                    * Every id here is one of the 48 in `art/manifest.json` and the content gate
                    * proves exactly-once coverage over `icons.legacy.*` on every build.
                    */}
                  <Art
                    src={A(b.artId)}
                    alt={b.name}
                    scrim="none"
                    style={{
                      height: 42,
                      border: `1px solid ${b.heldAt !== null ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                      filter: b.heldAt !== null ? undefined : 'grayscale(1) brightness(.45)',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  )
}

/** The pass surface itself, beside the bundle. Same rule, same closed line. */
function PassOffer({ data, onBuy, busy }: { data: LedgerPayload; onBuy: (sku: string) => void; busy: string | null }) {
  const surface = data.surfaces.find((s) => s.kind === 'pass')
  if (!surface) return null
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title={surface.label} icon="crate" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          The premium lane of the same forty rungs. Cosmetic on both tracks, permanently, and the
          free lane is never affected by whether anybody bought it.
        </p>
        <Btn
          v="gold"
          size="sm"
          full
          style={{ marginTop: 9 }}
          disabled={surface.closed || data.pass.owned || busy !== null}
          onClick={() => onBuy(surface.id)}
        >
          {data.pass.owned
            ? 'Pass Active'
            : surface.closed
              ? 'Not for sale'
              : busy === surface.id
                ? 'Opening the counter'
                : `Buy, $${surface.priceUsd.toFixed(2)}`}
        </Btn>
        {/*
          * THE CLOSED LINE, UNDER THE PASS AS WELL. It used to render only under the bundle, so
          * the pass button said "Not for sale" with no explanation anywhere near it.
          */}
        {surface.closed && data.pass.closedLine !== '' && (
          <p style={{ margin: '7px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            {data.pass.closedLine}
          </p>
        )}
      </div>
    </Panel>
  )
}

/* -------------------------------------------------------------------- screen */

export function Ledger() {
  const { era, now } = useGame()
  const data = useSession((s) => s.ledger)
  const load = useSession((s) => s.loadLedger)
  const loadHistory = useSession((s) => s.loadMoneyHistory)
  const [tab, setTab] = useState<TabId>('sheet')
  const [sel, setSel] = useState<{ t: number; lane: 'free' | 'prem' }>({ t: 0, lane: 'prem' })
  const [busy, setBusy] = useState<string | null>(null)
  const [fault, setFault] = useState<CounterLine | null>(null)

  const buy = (sku: string): void => {
    setFault(null)
    setBusy(sku)
    void startCheckout(sku).then((line) => {
      if (line) {
        setFault(line)
        setBusy(null)
      }
    })
  }

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  // The money history is its own read and its own tab, so it is fetched when that tab is opened
  // rather than on every visit to a screen most people come to for the reward track.
  useEffect(() => {
    if (tab === 'money') void loadHistory().catch(() => undefined)
  }, [tab, loadHistory])

  const { stacked } = useViewport()
  return (
    <Stack>
      {/* main column */}
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        {/* header */}
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 0', flexWrap: 'wrap', rowGap: 4 }}>
            <Icon n="ledger" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none', paddingBottom: 7 }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>The Ledger</div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>Season Pass and Legacy</div>
            </div>
            {/* `minWidth: 0` so the strip is allowed to shrink and scroll rather than shoving the
                season clock off the end of the panel. */}
            <div style={{ alignSelf: 'flex-end', minWidth: 0, flex: '1 1 auto' }}>
              <Tabs items={TABS} value={tab} onChange={setTab} />
            </div>
            <Spacer />
            <Row gap={7} style={{ flex: 'none', paddingBottom: 7 }}>
              <Icon n="clock" s={14} c="var(--gold)" />
              <div>
                <div className="t-label" style={{ fontSize: sz(8.5) }}>Season ends in</div>
                <div className="t-mono" style={{ fontSize: 12.5, color: 'var(--ink-hi)' }}>
                  {clock((era.endsAt - now) / 1000)}
                </div>
              </div>
              <div style={{ width: 1, height: 26, background: 'var(--line)' }} />
              <div>
                <div className="t-label" style={{ fontSize: sz(8.5) }}>Era {era.n}</div>
                <div className="t-mono" style={{ fontSize: 12.5, color: 'var(--gold)' }}>
                  Week {era.week} of {era.of}
                </div>
              </div>
            </Row>
          </Row>
        </Panel>

        {!data ? (
          <Panel style={{ flex: 1, minHeight: 0 }}>
            <FirstEra line="Reading the books" note="One moment." />
          </Panel>
        ) : (
          <>
            {tab === 'pass' && (
              <>
                <EraBanner data={data} />
                <RewardTrack data={data} sel={sel} setSel={setSel} />
                <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
                  <BadgeWall badges={data.badges} />
                  <Wardrobe data={data} />
                  <LegendScore data={data} />
                </Row>
              </>
            )}
            {tab === 'sheet' && <SheetTab data={data} />}
            {tab === 'store' && <StoreTab data={data} />}
            {tab === 'legacy' && <LegacyTab data={data} />}
            {tab === 'titles' && <TitlesTab />}
            {tab === 'bosses' && <BossesTab data={data} />}
            {tab === 'money' && <MoneyTab />}
            {tab === 'reveal' && <Reveal />}
          </>
        )}
      </Col>

      {/* right column. Hidden on the Counter, which draws every surface itself. */}
      {data && tab !== 'store' && (
        <Rail width={300}>
          <PassOffer data={data} onBuy={buy} busy={busy} />
          <Highlight data={data} onBuy={buy} busy={busy} />
          {fault && (
            <Panel tone={fault.tone === 'good' ? 'safe' : 'danger'} style={{ flex: 'none' }}>
              <div style={{ padding: '9px 11px', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
                {fault.text}
              </div>
            </Panel>
          )}
        </Rail>
      )}
    </Stack>
  )
}
